package com.zq.webproject.ui.payment

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Link
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.zq.webproject.utils.UrlLauncher

/**
 * 支付成功页面
 * @param orderNo 订单号
 * @param amount 支付金额
 * @param paymentMethod 支付方式（"balance" = 余额支付, "alipay" = 支付宝）
 * @param resourceUrl 资源链接（网盘地址，只有已支付订单才有值）
 * @param onBackToHome 返回首页回调
 */
@Composable
fun PaymentSuccessScreen(
    orderNo: String,
    amount: Double,
    paymentMethod: String = "balance",
    resourceUrl: String? = null,
    onBackToHome: () -> Unit
) {
    println("PaymentSuccessScreen - 渲染支付成功页面，订单号: $orderNo, 金额: $amount, 支付方式: $paymentMethod, 资源链接: $resourceUrl")
    
    // 支付方式显示文本
    val paymentMethodText = when (paymentMethod) {
        "alipay" -> "支付宝"
        "balance" -> "余额支付"
        else -> "未知方式"
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color(0xFFF5F5F5)),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(32.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // 成功图标
            Box(
                modifier = Modifier
                    .size(120.dp)
                    .background(Color(0xFF4CAF50), CircleShape),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = Icons.Default.CheckCircle,
                    contentDescription = "支付成功",
                    tint = Color.White,
                    modifier = Modifier.size(80.dp)
                )
            }

            Spacer(modifier = Modifier.height(32.dp))

            // 支付成功标题
            Text(
                text = "支付成功",
                fontSize = 28.sp,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF4CAF50)
            )

            Spacer(modifier = Modifier.height(16.dp))

            // 支付金额
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(text = "¥", fontSize = 24.sp, color = Color.Black)
                Text(
                    text = amount.toString(),
                    fontSize = 36.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color.Black
                )
            }

            Spacer(modifier = Modifier.height(24.dp))

            // 订单信息卡片
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
                shape = RoundedCornerShape(12.dp)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(20.dp)
                ) {
                    Text(
                        text = "订单信息",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Bold,
                        modifier = Modifier.padding(bottom = 12.dp)
                    )

                    InfoRow("订单号", orderNo)
                    InfoRow("支付方式", paymentMethodText)
                    InfoRow("支付状态", "已支付", Color(0xFF4CAF50))
                }
            }

            Spacer(modifier = Modifier.height(40.dp))

            // 百度网盘链接按钮（只有已支付订单且有资源链接时才显示）
            if (!resourceUrl.isNullOrEmpty()) {
                OutlinedButton(
                    onClick = {
                        println("PaymentSuccessScreen - 点击打开网盘链接: $resourceUrl")
                        UrlLauncher.openUrl(resourceUrl)
                    },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(50.dp),
                    shape = RoundedCornerShape(25.dp),
                    colors = ButtonDefaults.outlinedButtonColors(
                        contentColor = Color(0xFF1E88E5)
                    )
                ) {
                    Icon(
                        imageVector = Icons.Default.Link,
                        contentDescription = "链接",
                        modifier = Modifier.size(20.dp)
                    )
                    Spacer(modifier = Modifier.width(8.dp))
                    Text(
                        text = "查看资源链接",
                        fontSize = 16.sp,
                        fontWeight = FontWeight.Medium
                    )
                }

                Spacer(modifier = Modifier.height(16.dp))
            }

            // 返回首页按钮
            Button(
                onClick = {
                    println("PaymentSuccessScreen - 点击返回首页按钮")
                    onBackToHome()
                },
                modifier = Modifier
                    .fillMaxWidth()
                    .height(50.dp),
                colors = ButtonDefaults.buttonColors(
                    containerColor = Color(0xFF1E88E5)
                ),
                shape = RoundedCornerShape(25.dp)
            ) {
                Text(
                    text = "返回首页",
                    fontSize = 18.sp,
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}

@Composable
fun InfoRow(label: String, value: String, valueColor: Color = Color.Black) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 8.dp),
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        Text(
            text = label,
            fontSize = 14.sp,
            color = Color.Gray
        )
        Text(
            text = value,
            fontSize = 14.sp,
            color = valueColor,
            fontWeight = FontWeight.Medium
        )
    }
}